import { CSSProperties, useState } from "react";
import { IComponent } from "../../../models/Interfaces";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGripVertical, faMagnifyingGlass,
  faAlignLeft, faImage, faImages, faVideo,
  faHandPointer, faBars, faLayerGroup,
  faIdCard, faList, faTableColumns, faLocationDot,
  faShareNodes, faCode, faQuoteLeft, faNewspaper,
  faUsers, faTags, faCube, faClipboardList,
} from "@fortawesome/free-solid-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import React from "react";
import styles from "./designer.module.scss";
import ComponentHelpPopover from "./ComponentHelpPopover";

interface IComponentSidebar {
  components: Array<IComponent>;
}

/**
 * Returns a FontAwesome icon matched against the component's DisplayName.
 * Replace each case with a custom SVG/image import once icons are designed.
 */
const getComponentIcon = (displayName: string): IconDefinition => {
  const n = displayName.toLowerCase();
  if (n.includes("gallery") || n.includes("carousel") || n.includes("slider")) return faImages;
  if (n.includes("image") || n.includes("photo") || n.includes("picture"))    return faImage;
  if (n.includes("video"))                                                      return faVideo;
  if (n.includes("rich text") || n.includes("text") || n.includes("body") ||
      n.includes("content") || n.includes("paragraph"))                         return faAlignLeft;
  if (n.includes("form") || n.includes("contact") || n.includes("input"))      return faClipboardList;
  if (n.includes("button") || n.includes("cta") || n.includes("call to"))      return faHandPointer;
  if (n.includes("nav") || n.includes("menu"))                                  return faBars;
  if (n.includes("hero") || n.includes("banner") || n.includes("header"))      return faLayerGroup;
  if (n.includes("card") || n.includes("tile") || n.includes("feature"))       return faIdCard;
  if (n.includes("accordion") || n.includes("faq") || n.includes("collapse"))  return faList;
  if (n.includes("tab"))                                                        return faTableColumns;
  if (n.includes("map") || n.includes("location"))                             return faLocationDot;
  if (n.includes("social"))                                                     return faShareNodes;
  if (n.includes("embed") || n.includes("iframe") || n.includes("html") ||
      n.includes("code") || n.includes("script"))                               return faCode;
  if (n.includes("testimonial") || n.includes("review") || n.includes("quote")) return faQuoteLeft;
  if (n.includes("blog") || n.includes("post") || n.includes("news") ||
      n.includes("article"))                                                     return faNewspaper;
  if (n.includes("team") || n.includes("staff") || n.includes("people"))       return faUsers;
  if (n.includes("price") || n.includes("pricing") || n.includes("plan") ||
      n.includes("product"))                                                     return faTags;
  return faCube;
};

interface ISidebarComponentCard {
  component: IComponent;
  isHelpOpen: boolean;
  onHelpToggle: (nextOpen: boolean) => void;
}

const SidebarComponentCard = ({ component, isHelpOpen, onHelpToggle }: ISidebarComponentCard) => {
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const dragStyle: CSSProperties = {
    opacity: isDragging ? 0.35 : 1,
    cursor: isDragging ? "grabbing" : "grab",
    touchAction: "none"
  };

  const onDragStart = (event: React.DragEvent<HTMLDivElement>): void => {
    const serializedComponent = JSON.stringify(component);

    const card = event.currentTarget.getBoundingClientRect();

    event.dataTransfer.setData("text/plain", serializedComponent);
    event.dataTransfer.setData("component", serializedComponent);
    event.dataTransfer.setData("dragtype", "NEW_COMPONENT");
    event.dataTransfer.effectAllowed = "copyMove";
    document.body.dataset.sidebarDragging = "true";
    document.body.dataset.sidebarDragOffsetX = String(event.clientX - card.left);
    document.body.dataset.sidebarDragOffsetY = String(event.clientY - card.top);
    setIsDragging(true);
  };

  const onDragEnd = (): void => {
    delete document.body.dataset.sidebarDragging;
    delete document.body.dataset.sidebarDragOffsetX;
    delete document.body.dataset.sidebarDragOffsetY;
    setIsDragging(false);
  };

  return (
    <div
      draggable={true}
      className={`${styles.componentCard} AssignableComponent`}
      style={dragStyle}
      title={component.DisplayName}
      data-componentid={component.Id}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <FontAwesomeIcon icon={faGripVertical} className={styles.dragHandle} />
      <div className={styles.componentCardIcon}>
        <FontAwesomeIcon icon={getComponentIcon(component.DisplayName)} />
      </div>
      <span className={styles.componentCardName}>{component.DisplayName}</span>
      <ComponentHelpPopover component={component} isOpen={isHelpOpen} onToggle={onHelpToggle} />
    </div>
  );
};

const ComponentSidebar = ({ components }: IComponentSidebar) => {
  const [search, setSearch] = useState<string>("");
  const [openHelpComponentId, setOpenHelpComponentId] = useState<number | null>(null);

  const handleHelpToggle = (componentId: number, nextOpen: boolean): void => {
    setOpenHelpComponentId((currentOpenId) => {
      if (nextOpen) {
        return componentId;
      }

      // Ignore close events from components that are no longer the active popover.
      return currentOpenId === componentId ? null : currentOpenId;
    });
  };

  const filtered = components.filter(c =>
    c.DisplayName.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <aside className={`${styles.componentSidebar} mt-3`}>
      <div className={styles.sidebarTop}>
        <div className={styles.sidebarHeader}>Components</div>

      <div className={styles.sidebarSearch}>
        <div className={styles.searchWrapper}>
          <FontAwesomeIcon icon={faMagnifyingGlass} className={styles.searchIcon} />
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Filter..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>
      </div>

      <div className={styles.sidebarComponents}>
        {filtered.map(comp => (
          <SidebarComponentCard
            key={`sb-${comp.Id}`}
            component={comp}
            isHelpOpen={openHelpComponentId === comp.Id}
            onHelpToggle={(nextOpen) => handleHelpToggle(comp.Id, nextOpen)}
          />
        ))}

        {filtered.length === 0 && search.length > 0 && (
          <p className={styles.noResults}>No results for "{search}"</p>
        )}
      </div>
    </aside>
  );
};

export default ComponentSidebar;
